/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import type { ApplicationKey } from '@jira/platform__application-editions/src/application-key.tsx';
import { functionUnionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { useService } from '@jira/growth__jira-expansion-services/src/index.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { ToResolvedService } from '@atlassian/expansion-services/types';
import { requestAccess as requestAccessExpansionService } from '@atlassian/expansion-services/requestAccess';
import type {
	AccessRequestResponse,
	AccessRequestType,
	CrossJoinIntegration,
	CrossJoinTargetProduct,
} from '../../common/types.tsx';
import { fireErrorAnalytics, type CreateAnalyticsEvent } from '../analytics/index.tsx';

const URL = '/gateway/api/invitations/v1/access-requests/request';

export const requestAccess = async (
	product: CrossJoinTargetProduct,
	sourceProduct: ApplicationKey,
	cloudId: string,
	userId: string,
	integration: CrossJoinIntegration,
	createAnalyticsEvent: CreateAnalyticsEvent,
): Promise<AccessRequestType> => {
	const resource = `ari:cloud:${product}::site/${cloudId}`;

	try {
		const response = await fetchJson<AccessRequestResponse>(URL, {
			method: 'POST',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				resource,
				metadata: {
					touchpoint: 'Crossjoin Nudge',
					sourceProduct,
				},
			}),
		});
		const { results } = response;
		const result = results[resource];
		const analyticsAttributes = {
			userId,
			products: [product],
			inviteResult: [
				{
					resourceAri: resource,
					status: result,
				},
			],
			integration,
		};
		fireTrackAnalytics(
			createAnalyticsEvent({}),
			'productAccess requested',
			userId,
			analyticsAttributes,
		);
		return result;
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics(integration, error);
		throw new Error(`${integration} request access error`);
	}
};

export const useRequestAccessService: () => ToResolvedService<
	typeof requestAccessExpansionService
> | null = functionUnionWithCondition(
	() => expVal('expansion_services_in_cjn_jpd_and_loom', 'isEnabled', false),
	() => useService(requestAccessExpansionService),
	() => null,
);
